import { useCallback, useEffect, useState } from "react";

// Document Picture-in-Picture (Chrome and Edge 116+) opens a small window
// that stays on top of every other window, so the timer remains visible
// while the screen shows slides, a whiteboard or a video call. The app
// renders into it with a portal. Other browsers don't offer it; callers
// hide their button there.
export const pipSupported =
  typeof window !== "undefined" && "documentPictureInPicture" in window;

// The new window starts as an empty document. Linked stylesheets are linked
// again, so their fonts resolve against the stylesheet's own URL; inline
// ones (development builds) are copied rule by rule.
function copyStyles(target) {
  [...document.styleSheets].forEach((sheet) => {
    if (sheet.href) {
      const link = target.document.createElement("link");
      link.rel = "stylesheet";
      link.href = sheet.href;
      target.document.head.appendChild(link);
      return;
    }
    try {
      const style = target.document.createElement("style");
      style.textContent = [...sheet.cssRules]
        .map((rule) => rule.cssText)
        .join("\n");
      target.document.head.appendChild(style);
    } catch (error) {
      // A sheet from another origin can't be read; the mini timer works
      // without it.
    }
  });
}

// Returns { pipWindow, open, close }. pipWindow is the open window or
// null; open must be called from a click.
export default function usePictureInPicture({ width = 340, height = 230 } = {}) {
  const [pipWindow, setPipWindow] = useState(null);

  const open = useCallback(async () => {
    if (!pipSupported) {
      return;
    }
    try {
      const win = await window.documentPictureInPicture.requestWindow({
        width,
        height,
      });
      copyStyles(win);
      win.document.title = "focussuite.de";
      win.document.body.className = "mini-timer-body";
      win.addEventListener("pagehide", () => setPipWindow(null));
      setPipWindow(win);
    } catch (error) {
      // The browser refused (e.g. not started by a click); nothing opened.
    }
  }, [width, height]);

  const close = useCallback(() => pipWindow?.close(), [pipWindow]);

  // The window closes with the page that owns it, so it never shows a
  // timer that no longer updates.
  useEffect(() => () => pipWindow?.close(), [pipWindow]);

  return { pipWindow, open, close };
}
